import { infiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query'

import { FALLBACK_LANGUAGE, useLocalisation } from '@/utils/localisation'

import { useNotificationSortContext } from './useNotificationSortContext'
import { notificationsClient, NotificationsPageParams } from '../api/notifications.service'

export const notificationsQueryOptions = (passedInQueryParams?: Partial<NotificationsPageParams>) => {
  const queryParams: NotificationsPageParams = {
    orderByDesc: 'true',
    page: 1,
    pageSize: 20,
    ...passedInQueryParams,
  }

  return infiniteQueryOptions({
    queryKey: ['userNotifications', queryParams],
    queryFn: notificationsClient.getNotifications,
    initialPageParam: queryParams,
    getNextPageParam: (lastPage, allPages, lastPageParam): NotificationsPageParams | undefined => {
      const isNextPagePresent = allPages.length * lastPageParam.pageSize < lastPage.total
      return isNextPagePresent ? { ...queryParams, page: lastPage.page + 1 } : undefined
    },
    getPreviousPageParam: () => undefined,
    placeholderData: (prev) => prev,
  })
}

export const useNotifications = (queryParams?: Partial<NotificationsPageParams>) => {
  const { currentLanguage } = useLocalisation()
  const language = currentLanguage.substring(0, 2)
  const { sort } = useNotificationSortContext()

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetching, isLoading, data } = useSuspenseInfiniteQuery(
    notificationsQueryOptions({
      ...(queryParams ?? {}),
      language: language || FALLBACK_LANGUAGE,
      orderBy: sort.field,
      orderByDesc: sort.direction === 'desc' ? 'true' : 'false',
    })
  )
  const items = data?.pages.flatMap((page) => page.results) ?? []
  const count = data?.pages[0]?.total ?? 0
  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    items,
    count,
  }
}
